import { useState } from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import BeginnerHero from "@/components/beginner/BeginnerHero";
import BeginnerPainPoints from "@/components/beginner/BeginnerPainPoints";
import BeginnerSolution from "@/components/beginner/BeginnerSolution";
import BeginnerFeatures from "@/components/beginner/BeginnerFeatures";
import BeginnerFAQ from "@/components/beginner/BeginnerFAQ";
import BeginnerQuestionForm from "@/components/beginner/BeginnerQuestionForm";
import BeginnerCTA from "@/components/beginner/BeginnerCTA";
import { BookOpen, Clock, ThumbsUp, Play } from "lucide-react";
import { Button } from "@/components/ui/button";

const BeginnerAds = () => {
  const [videoPlaying, setVideoPlaying] = useState(false);
  
  const beginnerPainPoints = [
    {
      icon: BookOpen,
      title: "Feeling Completely Overwhelmed",
      description: "Google Ads feels like a foreign language with so many confusing options, settings, and technical terms that it's paralyzing to even know where to start.",
      iconColor: "text-red-500",
      borderColor: "red-200",
    },
    {
      icon: Clock,
      title: "Making Costly Mistakes",
      description: "Without guidance, it's terrifyingly easy to accidentally waste hundreds of dollars on the wrong keywords, wrong settings, or poor targeting.",
      iconColor: "text-red-500",
      borderColor: "red-200",
    },
    {
      icon: ThumbsUp,
      title: "Not Knowing Where to Start",
      description: "Every tutorial assumes you already know the basics, leaving true beginners lost and afraid to take the first step.",
      iconColor: "text-red-500",
      borderColor: "red-200",
    }
  ];
  
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Google Ads for Beginners | Stop Wasting Budget | Upfixe</title>
        <meta name="description" content="Start Google Ads with confidence. Our budget-smart AI prevents beginner mistakes, automates keyword filtering, and stops wasted ad spend before it happens." />
        <meta name="keywords" content="Google Ads tutorial for beginners, how to start Google Ads, Google Ads for small business beginners" />
        <meta property="og:title" content="Google Ads for Beginners | Stop Wasting Budget | Upfixe" />
        <meta property="og:description" content="Start Google Ads with confidence. Our budget-smart AI prevents beginner mistakes, automates keyword filtering, and stops wasted ad spend before it happens." />
        <link rel="canonical" href="https://upfixe.com/beginner" />
      </Helmet>
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <BeginnerHero />
        
        {/* Pain Points Section */}
        <BeginnerPainPoints 
          title={<>Why Google Ads feels <span className="text-blue-600">impossible</span> for beginners</>}
          description="If you've tried to create Google Ads before, you know how frustrating it can be. Here's why most beginners struggle:"
          painPoints={beginnerPainPoints}
        />
        
        {/* Solution Section */}
        <BeginnerSolution />
        
        {/* Video Demo Section */}
        <section className="py-24 px-4 bg-gradient-to-br from-background to-muted/50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                See How Easy Google Ads Can Be
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Watch how complete beginners create their first successful Google Ads campaign in minutes. 
                No experience needed—our AI guides you through everything.
              </p>
            </div>

            <div className="relative max-w-4xl mx-auto mb-12">
              <div className="aspect-video rounded-2xl overflow-hidden border-2 border-primary/20">
                <iframe
                  src="https://www.youtube.com/embed/2LXRIONSvoI"
                  title="Upfixe Beginner Demo Video"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={() => window.location.href = 'https://app.upfixe.com'}
                className="text-lg px-8"
              >
                Start Your First Campaign Free
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8"
                onClick={() => window.open('https://youtu.be/2LXRIONSvoI', '_blank')}
              >
                Watch Full Demo
              </Button>
            </div>
          </div>
        </section>
        
        {/* Feature Highlights */}
        <BeginnerFeatures />
        
        {/* FAQ Section */}
        <BeginnerFAQ />
        
        {/* Question Form */}
        <BeginnerQuestionForm />
        
        {/* CTA Section */}
        <BeginnerCTA />
      </main>

      <Footer />
    </div>
  );
};

export default BeginnerAds;
